import { useEffect, useMemo, useRef, useState } from "react";
import { watchVisiblePolling } from "./visiblePolling.mjs";

const INITIAL_STATE = { stores: null, error: "", refreshing: false };

function canView(operator) {
  return Number(operator?.id) === 40
    && operator?.role === "superadmin"
    && operator?.authKind !== "studio";
}

function normalizedPhone(phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  const local = digits.length > 11 && digits.startsWith("55") ? digits.slice(2) : digits;
  // Older records may omit the ninth digit used by Brazilian mobile numbers.
  if (local.length === 11 && local[2] === "9") return `${local.slice(0, 2)}${local.slice(3)}`;
  return local;
}

function uniqueStoreAlerts(alerts) {
  const seen = new Set();
  return (Array.isArray(alerts) ? alerts : []).filter((alert, index) => {
    const key = normalizedPhone(alert?.phone);
    if (!key) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleString("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function alertDescription(reason) {
  if (reason === "card_awaiting_manager_distribution") return "Aguardando distribuição pelo gerente";
  return "Proteção encontrou uma falha ou conversa sem cartão";
}

export default function InboundSafetyNotice({ operator, onBack }) {
  const authorized = canView(operator);
  const [state, setState] = useState(INITIAL_STATE);
  const refreshRef = useRef(null);

  useEffect(() => {
    if (!authorized) {
      setState(INITIAL_STATE);
      refreshRef.current = null;
      return undefined;
    }

    const controller = new AbortController();
    let inFlight = false;

    const reload = async () => {
      if (inFlight || controller.signal.aborted) return;
      inFlight = true;
      setState(previous => ({ ...previous, refreshing: true }));
      try {
        const response = await fetch("/api/inbound-safety/status", {
          credentials: "include",
          signal: controller.signal,
        });
        const data = await response.json();
        if (!response.ok || !data?.ok || !Array.isArray(data.stores)) {
          throw new Error("Não foi possível conferir os controles de segurança do WhatsApp.");
        }
        if (!controller.signal.aborted) {
          setState({ stores: data.stores, error: "", refreshing: false });
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          setState(previous => ({
            ...previous,
            error: error?.message || "Falha ao consultar os controles de segurança do WhatsApp.",
            refreshing: false,
          }));
        }
      } finally {
        inFlight = false;
      }
    };

    refreshRef.current = reload;
    const stop = watchVisiblePolling(reload, 30000);
    return () => {
      controller.abort();
      stop();
      refreshRef.current = null;
    };
  }, [authorized, operator?.id]);

  const stores = useMemo(
    () => (state.stores || []).map(store => ({ ...store, alerts: uniqueStoreAlerts(store.alerts) })),
    [state.stores],
  );
  const totalAlerts = stores.reduce((total, store) => total + store.alerts.length, 0);

  if (!authorized) return null;

  return (
    <main className="inbound-safety-page">
      <style>{`
        .inbound-safety-page {
          --safety-ink: #203b42;
          --safety-muted: #6c7d7d;
          --safety-line: #d9e4df;
          --safety-paper: #f5f8f4;
          --safety-panel: #fffefa;
          --safety-teal: #286a67;
          --safety-teal-soft: #e8f2ee;
          --safety-amber: #94611d;
          --safety-amber-soft: #faf1df;
          min-height: 100dvh;
          box-sizing: border-box;
          padding: clamp(20px, 4vw, 44px);
          background: var(--safety-paper);
          color: var(--safety-ink);
          font-family: inherit;
        }
        .inbound-safety-page *, .inbound-safety-page *::before, .inbound-safety-page *::after { box-sizing: border-box; }
        .safety-shell { width: min(100%, 1020px); margin: 0 auto; }
        .safety-topline { display: flex; align-items: center; justify-content: space-between; gap: 18px; margin-bottom: 28px; }
        .safety-back {
          display: inline-flex; align-items: center; gap: 9px; border: 1px solid var(--safety-line);
          border-radius: 8px; background: var(--safety-panel); color: var(--safety-ink); padding: 10px 14px;
          font: inherit; font-size: 14px; font-weight: 650; cursor: pointer; transition: background .16s ease, transform .16s ease;
        }
        .safety-back:hover { background: var(--safety-teal-soft); transform: translateY(-1px); }
        .safety-back:focus-visible, .safety-refresh:focus-visible { outline: 3px solid #8db8ad; outline-offset: 2px; }
        .safety-owner { color: var(--safety-muted); font-size: 12px; letter-spacing: .08em; text-transform: uppercase; font-weight: 700; }
        .safety-heading { display: flex; align-items: flex-end; justify-content: space-between; gap: 24px; padding-bottom: 24px; border-bottom: 1px solid var(--safety-line); }
        .safety-eyebrow { margin: 0 0 8px; color: var(--safety-teal); font-size: 12px; font-weight: 750; letter-spacing: .12em; text-transform: uppercase; }
        .safety-heading h1 { margin: 0; font-size: clamp(28px, 4vw, 38px); line-height: 1.1; letter-spacing: -.035em; font-weight: 720; }
        .safety-subtitle { margin: 10px 0 0; color: var(--safety-muted); font-size: 15px; line-height: 1.55; }
        .safety-refresh {
          flex: none; display: inline-flex; align-items: center; justify-content: center; gap: 9px;
          min-height: 42px; padding: 0 16px; border: 0; border-radius: 8px; background: var(--safety-teal);
          color: #fffefa; font: inherit; font-weight: 680; cursor: pointer; transition: background .16s ease, transform .16s ease;
        }
        .safety-refresh:hover:not(:disabled) { background: #1e5755; transform: translateY(-1px); }
        .safety-refresh:disabled { cursor: wait; opacity: .72; }
        .safety-overview { display: flex; align-items: center; gap: 12px; margin: 22px 0 16px; color: var(--safety-muted); font-size: 13px; }
        .safety-count { display: inline-flex; align-items: center; gap: 7px; border-radius: 99px; background: var(--safety-amber-soft); color: #80551b; padding: 6px 11px; font-weight: 700; }
        .safety-count.is-clear { background: var(--safety-teal-soft); color: var(--safety-teal); }
        .safety-panel { border: 1px solid var(--safety-line); border-radius: 12px; background: var(--safety-panel); box-shadow: 0 8px 24px rgba(42, 76, 69, .045); }
        .safety-loading { padding: 22px; }
        .safety-skeleton { height: 15px; margin: 10px 0; border-radius: 6px; background: linear-gradient(90deg, #e7eeea 20%, #f1f5f2 45%, #e7eeea 70%); background-size: 220% 100%; animation: safety-sheen 1.4s ease-in-out infinite; }
        .safety-skeleton:nth-child(2) { width: 72%; }
        .safety-error { display: flex; align-items: flex-start; gap: 13px; margin: 18px 0; padding: 16px 18px; border: 1px solid #e8c9a1; border-radius: 10px; background: #fff8ed; color: #764d1e; }
        .safety-error strong { display: block; margin-bottom: 4px; }
        .safety-error p { margin: 0; font-size: 14px; line-height: 1.5; }
        .safety-store { margin-top: 14px; overflow: hidden; }
        .safety-store-head { display: flex; justify-content: space-between; align-items: center; gap: 16px; padding: 17px 20px; background: #f9fbf8; border-bottom: 1px solid var(--safety-line); }
        .safety-store-head h2 { margin: 0; font-size: 17px; letter-spacing: -.01em; }
        .safety-store-id { color: var(--safety-muted); font: 12px ui-monospace, SFMono-Regular, Menlo, monospace; }
        .safety-store-body { padding: 4px 20px; }
        .safety-note { display: grid; grid-template-columns: 174px 1fr; gap: 18px; padding: 15px 0; border-bottom: 1px solid #eaf0ec; }
        .safety-note:last-child { border-bottom: 0; }
        .safety-note-label { color: var(--safety-muted); font-size: 12px; font-weight: 750; letter-spacing: .055em; text-transform: uppercase; }
        .safety-note-copy { font-size: 14px; line-height: 1.55; }
        .safety-note-copy p { margin: 0 0 6px; }
        .safety-note-copy p:last-child { margin-bottom: 0; }
        .safety-alert-list { list-style: none; padding: 0; margin: 0; }
        .safety-alert-list li { display: flex; flex-wrap: wrap; justify-content: space-between; gap: 8px 18px; padding: 8px 0; border-bottom: 1px solid #eef2ef; }
        .safety-alert-list li:last-child { border-bottom: 0; }
        .safety-alert-phone { font-weight: 700; font-variant-numeric: tabular-nums; }
        .safety-alert-reason { color: #536765; }
        .safety-alert-time { flex: none; color: var(--safety-muted); font-size: 12px; }
        .safety-empty { padding: 32px 24px; text-align: center; }
        .safety-empty-mark { display: grid; place-items: center; width: 42px; height: 42px; margin: 0 auto 12px; border-radius: 50%; background: var(--safety-teal-soft); color: var(--safety-teal); font-size: 19px; font-weight: 700; }
        .safety-empty h2 { margin: 0; font-size: 17px; }
        .safety-empty p { margin: 7px auto 0; max-width: 480px; color: var(--safety-muted); font-size: 14px; line-height: 1.5; }
        @keyframes safety-sheen { from { background-position: 100% 0; } to { background-position: -120% 0; } }
        @media (max-width: 640px) {
          .inbound-safety-page { padding: 18px 15px 32px; }
          .safety-topline { margin-bottom: 24px; }
          .safety-owner { font-size: 10px; text-align: right; }
          .safety-heading { align-items: flex-start; flex-direction: column; gap: 18px; }
          .safety-refresh { width: 100%; }
          .safety-note { grid-template-columns: 1fr; gap: 7px; }
          .safety-store-head, .safety-store-body { padding-left: 15px; padding-right: 15px; }
          .safety-alert-time { width: 100%; padding-left: 0; }
        }
        @media (prefers-reduced-motion: reduce) { .safety-skeleton { animation: none; } }
      `}</style>
      <div className="safety-shell">
        <div className="safety-topline">
          {typeof onBack === "function"
            ? <button className="safety-back" type="button" onClick={onBack}><span aria-hidden="true">←</span> Voltar</button>
            : <span />}
          <span className="safety-owner">Área da proprietária</span>
        </div>

        <header className="safety-heading">
          <div>
            <p className="safety-eyebrow">Proteção operacional</p>
            <h1>Alertas do WhatsApp</h1>
            <p className="safety-subtitle">Acompanhe números sem cartão e os controles ativos em cada loja.</p>
          </div>
          <button className="safety-refresh" type="button" onClick={() => refreshRef.current?.()} disabled={state.refreshing}>
            <span aria-hidden="true">↻</span> {state.refreshing ? "Atualizando…" : "Atualizar"}
          </button>
        </header>

        {state.stores === null && !state.error ? (
          <section className="safety-panel safety-loading" aria-label="Carregando alertas" aria-busy="true">
            <div className="safety-skeleton" /><div className="safety-skeleton" /><div className="safety-skeleton" />
          </section>
        ) : null}

        {state.error ? (
          <section className="safety-error" role="alert">
            <span aria-hidden="true">!</span>
            <div>
              <strong>Não foi possível confirmar a situação</strong>
              <p>{state.error} A ausência de informação não significa operação normal. Tente atualizar novamente.</p>
            </div>
          </section>
        ) : null}

        {state.stores !== null && !state.error ? (
          <>
            <div className="safety-overview">
              <span className={`safety-count${totalAlerts ? "" : " is-clear"}`}>
                {totalAlerts ? `${totalAlerts} ${totalAlerts === 1 ? "número pendente" : "números pendentes"}` : "Nenhum número pendente"}
              </span>
              <span>Leitura automática a cada 30 segundos enquanto a página estiver visível.</span>
            </div>
            {stores.length === 0 ? (
              <section className="safety-panel safety-empty">
                <span className="safety-empty-mark" aria-hidden="true">—</span>
                <h2>Nenhuma loja retornada</h2>
                <p>Não há informações de segurança disponíveis neste momento. Atualize para consultar novamente.</p>
              </section>
            ) : stores.map(store => (
              <section className="safety-panel safety-store" key={store.storeId}>
                <div className="safety-store-head">
                  <h2>{store.name || `Loja ${store.storeId}`}</h2>
                  <span className="safety-store-id">ID {store.storeId}</span>
                </div>
                <div className="safety-store-body">
                  {store.testMode ? (
                    <div className="safety-note">
                      <div className="safety-note-label">Modo da operação</div>
                      <div className="safety-note-copy">Modo teste ativo. O cadastro de novos contatos continua protegido; este controle não é a pausa geral de envios.</div>
                    </div>
                  ) : null}
                  {store.configurationUnavailable ? (
                    <div className="safety-note">
                      <div className="safety-note-label">Configuração</div>
                      <div className="safety-note-copy">Falha ao ler a configuração. A última opção conhecida foi mantida; a automação segue restrita até uma nova leitura.</div>
                    </div>
                  ) : null}
                  {store.storeId === 4 && store.automaticPaused ? (
                    <div className="safety-note">
                      <div className="safety-note-label">Pausa automática</div>
                      <div className="safety-note-copy">Follow-up automático pausado até autorização da dona. Não reativar em publicação ou reinício.</div>
                    </div>
                  ) : null}
                  {store.alerts.length ? (
                    <div className="safety-note">
                      <div className="safety-note-label">Números pendentes</div>
                      <div className="safety-note-copy">
                        <ul className="safety-alert-list">
                          {store.alerts.map((alert, index) => (
                            <li key={`${normalizedPhone(alert.phone) || "sem-numero"}-${index}`}>
                              <span className="safety-alert-phone">{alert.phone || "Número não informado"}</span>
                              <span className="safety-alert-reason">{alertDescription(alert.reason)}</span>
                              {formatDate(alert.at) ? <time className="safety-alert-time">{formatDate(alert.at)}</time> : null}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ) : null}
                  {!store.testMode && !store.configurationUnavailable && !(store.storeId === 4 && store.automaticPaused) && !store.alerts.length ? (
                    <div className="safety-note">
                      <div className="safety-note-label">Situação</div>
                      <div className="safety-note-copy">Nenhum alerta pendente informado para esta loja.</div>
                    </div>
                  ) : null}
                </div>
              </section>
            ))}
          </>
        ) : null}
      </div>
    </main>
  );
}
